import './bootstrap';
import { createApp, DefineComponent, h } from 'vue';
import { createInertiaApp, usePage } from '@inertiajs/vue3';
import PusherService from '@/app/services/PusherService';
import * as Sentry from '@sentry/vue';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import VueAxios from 'vue-axios';
import axios from 'axios';
import '@/assets/css/style.scss';
import { PageProps } from './types';
import { inertiaVersionCheck, setupNavigationHandlers } from '@/utils/inertia';
import { createI18n } from 'vue-i18n';
import messages from '@/app/lang/index';
import { textFitDirective } from './utils/textFit';
import { clickOutsideDirective } from './utils/clickOutside';
import 'froala-editor/css/froala_style.min.css';
import { initNetworkMonitoring } from './utils/networkMonitor';
import { setupTouchMode } from './utils/touchMode';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';
const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || null;
const VITE_SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local';
const VITE_SENTRY_TRACES_SAMPLE_RATE = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.3;

const i18n = createI18n({
    legacy: false,
    locale: 'ko',
    messages: messages,
    warnHtmlMessage: false,
});
window.$t = i18n.global.t;

// PWA 설치 프롬프트 이벤트 처리
window.addEventListener('beforeinstallprompt', (e) => {
    // 기본 설치 배너가 뜨지 않도록 막음
    e.preventDefault();
    // 이벤트를 window 객체에 저장하여 모든 컴포넌트가 접근할 수 있도록 함
    window.__deferredPrompt = e;
});

// PWA 설치 완료 이벤트 처리
window.addEventListener('appinstalled', (e) => {
    window.__deferredPrompt = null;
});

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: async (name) => {
        // 페이지 컴포넌트 로드
        return resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>('./pages/**/*.vue'));
    },
    setup({ el, App, props, plugin }) {
        // Inertia.js 스크롤 복원 오류 방지를 위한 안전장치
        if (typeof window !== 'undefined' && !window.history.state) {
            window.history.replaceState({ scrollRegions: [] }, '', window.location.href);
        }

        const app = createApp({ render: () => h(App, props) });

        // Sentry 초기화 추가
        if (VITE_SENTRY_DSN) {
            Sentry.init({
                app,
                dsn: VITE_SENTRY_DSN,
                environment: VITE_SENTRY_ENVIRONMENT,
                tracesSampleRate: VITE_SENTRY_TRACES_SAMPLE_RATE,
                beforeSend(event) {
                    const pageData = usePage<PageProps>();
                    const user = pageData.props.auth.user;
                    // 유저 정보 추가
                    if (user) {
                        event.user = {
                            ...event.user,
                            ...user,
                            guard: pageData.props.auth.guard,
                        };
                    }

                    // API 실패(axios 등) 에러 필터링 - values 전체 순회
                    if (event.exception && Array.isArray(event.exception.values)) {
                        for (const exception of event.exception.values) {
                            // Cancel 에러 필터링 (요청 취소는 정상적인 동작)
                            if (
                                exception.value?.includes('요청 취소') ||
                                exception.value?.includes('Cancel') ||
                                exception.type === 'AxiosError' ||
                                exception.value?.includes('Network Error') ||
                                exception.value?.includes('Request failed with status code')
                            ) {
                                return null;
                            }
                        }
                    }
                    return event;
                },
            });
        }

        app.use(plugin);
        app.use(ZiggyVue);
        app.use(i18n);
        app.config.globalProperties.$t = i18n.global.t;

        // axios 설정
        app.use(VueAxios, axios);
        app.axios.interceptors.response.use(
            (response) => {
                inertiaVersionCheck(response);

                return Promise.resolve(response);
            },
            (error) => {
                return Promise.reject(error);
            },
        );

        app.directive('textfit', textFitDirective);
        app.directive('click-outside', clickOutsideDirective);
        app.mount(el);

        // 채널 구독
        const pageData = usePage<any>();
        if (pageData.props?.channel) {
            PusherService.getInstance().subscribeClassChannel(pageData.props.channel, pageData.props.private_channel);
        }

        initNetworkMonitoring();

        // 터치 이벤트와 마우스 이벤트를 구분하여 터치 모드 관리
        setupTouchMode();

        window.addEventListener('online', () => {
            window.location.reload();
        });

        // 뒤로가기/앞으로가기 시 서버에서 데이터 재호출 처리
        setupNavigationHandlers();
    },
    progress: {
        color: '#4B5563',
    },
});
